import * as THREE from 'three';
import type { WorldTheme } from '../core/types';
import { createEnvironment, updateEnvironment } from './environment';
import { WORLD_THEMES } from './palette';

export class GameScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly boardAnchor = new THREE.Group();
  private keyLight: THREE.DirectionalLight;
  private fillLight: THREE.DirectionalLight;
  private ambient: THREE.AmbientLight;
  private rim: THREE.PointLight;
  private bgMesh: THREE.Mesh | null = null;
  private particles: THREE.Points | null = null;
  private environment: THREE.Group | null = null;
  private quality: 'low' | 'medium' | 'high' = 'high';
  private reducedMotion = false;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;

    this.camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, -1.2, 13.5);
    this.camera.lookAt(0, 0.3, 0);

    this.ambient = new THREE.AmbientLight(0xffffff, 0.45);
    this.keyLight = new THREE.DirectionalLight(0xffffff, 1.1);
    this.keyLight.position.set(4, 8, 10);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.mapSize.set(1024, 1024);
    this.fillLight = new THREE.DirectionalLight(0xaaccff, 0.35);
    this.fillLight.position.set(-6, -2, 4);
    this.rim = new THREE.PointLight(0xffffff, 0.6, 30);
    this.rim.position.set(0, 0, 6);

    this.scene.add(this.ambient, this.keyLight, this.fillLight, this.rim);
    this.scene.add(this.boardAnchor);

    this.applyTheme('ember');
    window.addEventListener('resize', () => this.resize());
  }

  applyTheme(theme: WorldTheme): void {
    const t = WORLD_THEMES[theme];
    this.scene.background = new THREE.Color(t.bgTop);
    this.scene.fog = new THREE.Fog(t.fog, 14, 32);
    this.ambient.color.setHex(t.ambient);
    this.keyLight.color.setHex(t.key);
    this.rim.color.setHex(t.rim);

    if (this.bgMesh) {
      this.scene.remove(this.bgMesh);
      (this.bgMesh.material as THREE.Material).dispose();
      this.bgMesh.geometry.dispose();
    }

    // Soft gradient plane
    const canvas = document.createElement('canvas');
    canvas.width = 8;
    canvas.height = 256;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 0, 0, 256);
    const c1 = new THREE.Color(t.bgTop);
    const c2 = new THREE.Color(t.bgBottom);
    grad.addColorStop(0, `#${c1.getHexString()}`);
    grad.addColorStop(1, `#${c2.getHexString()}`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 8, 256);
    const tex = new THREE.CanvasTexture(canvas);
    this.bgMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(40, 40),
      new THREE.MeshBasicMaterial({ map: tex, depthWrite: false })
    );
    this.bgMesh.position.z = -8;
    this.scene.add(this.bgMesh);

    this.buildAmbientParticles(theme);

    if (this.environment) {
      this.scene.remove(this.environment);
      this.environment = null;
    }
    this.environment = createEnvironment(theme);
    this.scene.add(this.environment);
  }

  private buildAmbientParticles(theme: WorldTheme): void {
    if (this.particles) {
      this.scene.remove(this.particles);
      this.particles.geometry.dispose();
      (this.particles.material as THREE.Material).dispose();
      this.particles = null;
    }
    if (this.quality === 'low' || this.reducedMotion) return;

    const count = this.quality === 'high' ? 80 : 40;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 16;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 12;
      positions[i * 3 + 2] = -2 - Math.random() * 4;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const colors: Record<WorldTheme, number> = {
      ember: 0xffaa66,
      tide: 0x66e0e8,
      gale: 0xa0c8ff,
      root: 0x90d070,
      void: 0xc0a0ff,
    };
    this.particles = new THREE.Points(
      geo,
      new THREE.PointsMaterial({
        color: colors[theme],
        size: 0.08,
        transparent: true,
        opacity: 0.45,
        depthWrite: false,
      })
    );
    this.scene.add(this.particles);
  }

  setQuality(q: 'low' | 'medium' | 'high'): void {
    this.quality = q;
    const pr = q === 'high' ? Math.min(window.devicePixelRatio, 2) : q === 'medium' ? 1.25 : 1;
    this.renderer.setPixelRatio(pr);
    this.renderer.shadowMap.enabled = q !== 'low';
    this.keyLight.castShadow = q !== 'low';
  }

  setReducedMotion(v: boolean): void {
    this.reducedMotion = v;
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    // Zoom out slightly on tall mobile
    const base = 12;
    const extra = h > w ? 1.5 : 0;
    this.camera.position.z = base + extra;
  }

  update(time: number): void {
    if (this.particles && !this.reducedMotion) {
      this.particles.rotation.z = time * 0.02;
      const pos = this.particles.geometry.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < pos.count; i++) {
        let y = pos.getY(i) + 0.003;
        if (y > 6) y = -6;
        pos.setY(i, y);
      }
      pos.needsUpdate = true;
    }
    if (this.environment) {
      updateEnvironment(this.environment, time, this.reducedMotion);
    }
    // gentle camera breathing
    if (!this.reducedMotion) {
      this.camera.position.x = Math.sin(time * 0.15) * 0.08;
      this.camera.position.y = -1.2 + Math.cos(time * 0.12) * 0.05;
      this.camera.lookAt(0, 0.3, 0);
    }
  }

  /** Brief controlled dolly for big moments — never disorienting. */
  pulseCamera(amount = 0.15): void {
    if (this.reducedMotion) return;
    this.camera.position.z -= amount;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.renderer.dispose();
  }
}
